import React from 'react';
import Header from '@/components/Header';
import ManufacturerLogos from '@/components/ManufacturerLogos';
import Footer from '@/components/sections/Footer';

export default function CookiePolicyPage() {
  return (
    <div className="min-h-screen">
      <Header />
      <ManufacturerLogos />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">Cookie Policy</h1>
          <p className="text-muted-foreground mb-8">Last updated: January 2025</p>

          <div className="prose prose-lg max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-bold mb-4">1. What Are Cookies</h2>
              <p className="text-muted-foreground leading-relaxed">
                Cookies are small text files that are stored on your device when you visit a website. They help websites remember your preferences and provide a better user experience.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">2. How We Use Cookies</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                UAVsForSale.com uses cookies for the following purposes:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li><strong>Essential Cookies:</strong> Required for the website to function properly (e.g., maintaining your session)</li>
                <li><strong>Functional Cookies:</strong> Remember your preferences and settings</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our website</li>
                <li><strong>Marketing Cookies:</strong> Used to deliver relevant advertisements</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">3. Types of Cookies We Use</h2>
              
              <h3 className="text-xl font-semibold mb-3">3.1 First-Party Cookies</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                These are cookies set by UAVsForSale.com:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li><strong>Session Cookies:</strong> Temporary cookies that expire when you close your browser</li>
                <li><strong>Authentication Cookies:</strong> Keep you logged in to your account</li>
                <li><strong>Preference Cookies:</strong> Remember your language and display preferences</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">3.2 Third-Party Cookies</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We use services from third parties that may set cookies:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li><strong>Analytics:</strong> Google Analytics to understand website usage</li>
                <li><strong>Advertising:</strong> Marketing platforms to show relevant ads</li>
                <li><strong>Social Media:</strong> Sharing buttons and embedded content</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">4. Cookie Details</h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-border">
                  <thead>
                    <tr className="bg-muted">
                      <th className="border border-border p-3 text-left font-semibold">Cookie Name</th>
                      <th className="border border-border p-3 text-left font-semibold">Purpose</th>
                      <th className="border border-border p-3 text-left font-semibold">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="text-muted-foreground">
                    <tr>
                      <td className="border border-border p-3">session_id</td>
                      <td className="border border-border p-3">Maintains your session</td>
                      <td className="border border-border p-3">Session</td>
                    </tr>
                    <tr>
                      <td className="border border-border p-3">preferences</td>
                      <td className="border border-border p-3">Stores your settings</td>
                      <td className="border border-border p-3">1 year</td>
                    </tr>
                    <tr>
                      <td className="border border-border p-3">_ga</td>
                      <td className="border border-border p-3">Google Analytics tracking</td>
                      <td className="border border-border p-3">2 years</td>
                    </tr>
                    <tr>
                      <td className="border border-border p-3">marketing_consent</td>
                      <td className="border border-border p-3">Tracks your cookie preferences</td>
                      <td className="border border-border p-3">1 year</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">5. Managing Cookies</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                You have several options to manage cookies:
              </p>
              
              <h3 className="text-xl font-semibold mb-3">5.1 Browser Settings</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Most web browsers allow you to control cookies through their settings. You can:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Block all cookies</li>
                <li>Accept only first-party cookies</li>
                <li>Delete cookies when you close your browser</li>
                <li>View and delete existing cookies</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">5.2 Browser-Specific Instructions</h3>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li><strong>Chrome:</strong> Settings → Privacy and security → Cookies and other site data</li>
                <li><strong>Firefox:</strong> Options → Privacy & Security → Cookies and Site Data</li>
                <li><strong>Safari:</strong> Preferences → Privacy → Cookies and website data</li>
                <li><strong>Edge:</strong> Settings → Privacy, search, and services → Cookies</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">5.3 Opt-Out Tools</h3>
              <p className="text-muted-foreground leading-relaxed">
                You can opt out of certain cookies using these tools:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Google Analytics Opt-out: <a href="https://tools.google.com/dlpage/gaoptout" className="text-primary hover:underline">tools.google.com/dlpage/gaoptout</a></li>
                <li>Network Advertising Initiative: <a href="https://optout.networkadvertising.org" className="text-primary hover:underline">optout.networkadvertising.org</a></li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">6. Impact of Disabling Cookies</h2>
              <p className="text-muted-foreground leading-relaxed">
                If you disable or refuse cookies, some parts of the website may not function properly. Essential cookies are required for the website to work, and blocking them may prevent you from using certain features.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">7. Updates to This Policy</h2>
              <p className="text-muted-foreground leading-relaxed">
                We may update this Cookie Policy from time to time to reflect changes in our practices or legal requirements. Please check this page periodically for updates.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">8. More Information</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                For more information about how we use your data, please see our <a href="/privacy-policy" className="text-primary hover:underline">Privacy Policy</a>.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                If you have questions about our use of cookies, contact us at:
              </p>
              <div className="mt-4 text-muted-foreground">
                <p><strong>Email:</strong> privacy@uavsforsale.com</p>
                <p><strong>Address:</strong> UAVsForSale.com, San Francisco, CA</p>
              </div>
            </section>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}